import { ChannelService } from '../../core/service/channel.service';
import { FlowableService } from 'src/app/core/service/flowable.service';
import { Component, OnInit, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { AppConfiguration, AppConfigService } from 'src/app/core/service/app-config.service';
import { NGXLogger } from 'ngx-logger';
import { IdleService } from 'src/app/core/service/idle.service';
import { SettingService } from 'src/app/core/service/setting.service';
import { Channel, ChannelData } from 'src/app/channel/model/channel';
import { User } from 'src/app/user/model/user';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';

/**
 * Angular component class for Channel modeler.
 */
@Component({
  standalone: false,
  selector: 'app-channel-modeler',
  templateUrl: './channel-modeler.component.html',
  styleUrls: ['./channel-modeler.component.scss']
})
export class ChannelModelerComponent implements OnInit, OnDestroy
{
  @ViewChild('iframeModeler') public iframe: ElementRef;

  /**
   * URL for Flowable modeler UI app.
   */
  public url: SafeResourceUrl;

  /**
   * Data model object for this channel.
   */
  public channel: Channel = null;
  public loading = true;
  public deployingProcess = false;
  public patientLabel: string;

  /**
   * Channel ID
   */
  private id: number;

  /**
   * Model ID
   */
  private modelId: string;
  private idInterval: any;
  private modelerAutoSave: number;

  /**
   * Flowable designer process model.
   */
  private bpmn: any = null;

  /**
   * Deployed (versioned) executable BPMN.
   */
  private deployedBpmn: any = null;

  /**
   * Flowable app model.
   */
  private flowableApp: any = null;

  private appConfig: AppConfiguration;

  /**
   * Constructor.
   */
  constructor(public sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private router: Router,
    private logger: NGXLogger,
    private messageHandlerService: MessageHandlerService,
    private flowableService: FlowableService,
    private appConfigService: AppConfigService,
    private idleService: IdleService,
    private settingService: SettingService,
    private channelService: ChannelService,
    private authService: AuthenticationService)
  {
    this.appConfig = this.appConfigService.getConfig();
  }

  /**
   * Component initialization.
   */
  public ngOnInit(): void
  {
    this.loading = true;
    this.getSessionSettings();
    this.idleService.stopIdle();
    this.url = `${this.appConfig.flowable.baseUrl}/flowable-modeler/#/editor/`;

    // Check if a resource identifier is present
    this.route.params.forEach((params: Params) =>
    {
      // Channel ID
      if (params.id !== undefined)
      {
        this.id = params.id;
        // retrieve channel data
        this.channelService.getChannel(this.id).subscribe({
          next: (data) =>
          {
            this.channel = data;
            this.manageLoad();
            if (this.channel.appId !== null)
            {
              // retrieve the Flowable app
              this.flowableService.getModel(this.channel.appId).subscribe({
                next: (app) =>
                {
                  this.flowableApp = app;
                }
              });
            }
          }
        });
      }

      // BPM model ID
      if (params.modelId !== undefined)
      {
        this.modelId = params.modelId;

        this.url = this.sanitizer.bypassSecurityTrustResourceUrl(this.url.toString() + this.modelId);

        // retrieve the modeler's BPMN
        this.flowableService.getModel(this.modelId).subscribe({
          next: (data) =>
          {
            this.bpmn = data;
          }
        });
      }
    });
  }

  /**
   * Navigates to the channel edit component.
   */
  public backToChannelEdit(): void
  {
    this.router.navigateByUrl(`/channel-edit/${this.id}`);
  }

  /**
   * Deploy the BPMN.
   */
  public deployProcess(): void
  {
    const nativeEl = this.iframe.nativeElement;
    if (this.flowableApp === null)
    {
      // step 1: create a new app
      // nativeEl.contentWindow.save();
      const newApp =
      {
        name: `App - ${this.bpmn.name}`,
        key: `app:${this.bpmn.key}:ver${this.channel.version}`,
        description: `Flowable application for ${this.bpmn.name}`,
        modelType: 3 // type 3 is an app
      };

      this.deployingProcess = true;
      this.flowableService.createApp(newApp).subscribe({
        next: (data) =>
        {
          this.flowableApp = data;

          // step 2: update the app to include the model and publish
          const cont = nativeEl.contentWindow || nativeEl.contentDocument;
          const channelAutosave = new ChannelData();
          channelAutosave.operation = 'save-model';
          cont.postMessage('save-model', '*');
          const updatedApp = this.includeBpm(this.flowableApp, this.bpmn);
          setTimeout(this.saveAppDetailed, 1000, updatedApp, this.flowableApp, this.flowableService,
            this.messageHandlerService, this.channelService, this.channel);
        },
        error: (error) =>
        {
          this.logger.error(error);
          this.messageHandlerService.displayError('FAILED-TO-DEPLOY-PROCESS');
        },
        complete: () =>
        {
          this.deployingProcess = false;
        }
      });
    }
    else
    {
      const cont = nativeEl.contentWindow || nativeEl.contentDocument;
      const channelAutosave = new ChannelData();
      channelAutosave.operation = 'save-model';
      cont.postMessage('save-model', '*');
      const updatedApp = this.includeBpm(this.flowableApp, this.bpmn);
      setTimeout(this.saveAppDetailed, 1000, updatedApp, this.flowableApp, this.flowableService,
        this.messageHandlerService, this.channelService, this.channel);
    }
  }

  /**
   * Include the bpmn in the flowable app and return the app object.
   *
   * @param flowableApp Flowable app
   * @param bpmn BPMN object
   */
  public includeBpm(flowableApp, bpmn): any
  {
    return {
      appDefinition:
      {
        id: flowableApp.id,
        name: flowableApp.name,
        key: flowableApp.key,
        description: flowableApp.description,
        version: flowableApp.version,
        definition: // this is the BPM
        {
          models: [
            bpmn // we only support a single process for each channel
          ],
          theme: 'theme-1', // default theme color
          icon: 'glyphicon-asterisk' // default icon
        }
      },
      publish: true // must have this property to publish the app.
    };
  }

  /**
   * Save the flowable app in the database, and update references in channel.
   *
   * @param updatedApp The flowable app to save
   */
  public saveApp(updatedApp): void
  {
    this.flowableService.updateApp(this.flowableApp.id, updatedApp).subscribe({
      next: (appData) =>
      {
        if (appData.appDefinition !== null)
        {
          this.flowableApp = appData.appDefinition; // Flowable API wraps the app data inside "appDefinition".
          const key = appData.appDefinition.key;
          const id = appData.appDefinition.id;

          // step 3: get the deployed BPMN by the app's key.
          this.flowableService.getDeployedBpmnByAppKey(key).subscribe({
            next: (bpmn) =>
            {
              this.deployedBpmn = bpmn.data[0]; // Flowable API wraps the models in a data[] array
              this.messageHandlerService.displaySuccess(
                'DEPLOYED-SUCCESSFULLY'
              );

              // step 4: save the deployed BPMN id and key in channel.
              this.deployedBpmn.appId = id;
              this.channelService.updateModel(this.channel.id, this.deployedBpmn).subscribe({
                next: (channel) =>
                {
                  this.channel = channel;
                }
              });
            },
            error: (error) =>
            {
              this.logger.error(error);
              this.messageHandlerService.displayError('FAILED-TO-DEPLOY-PROCESS');
            }
          });
        }
        else if (appData.error)
        {
          this.messageHandlerService.displayError('FAILED-TO-VALIDATE-PROCESS');
        }
      },
      error: (error) =>
      {
        this.logger.error(error);
        this.messageHandlerService.displayError('FAILED-TO-DEPLOY-PROCESS');
      }
    });
  }

  /**
   * Save the flowable app in the database, and update references in channel.
   *
   * @param updatedApp The flowable app to save
   */
  public saveAppDetailed(updatedAppDetailed, flowableAppDetailed, flowableServiceDetailed, messageHandlerServiceDetailed,
    channelServiceDetailed, channelDetailed): void
  {
    flowableServiceDetailed.updateApp(flowableAppDetailed.id, updatedAppDetailed).subscribe({
      next: (appData) =>
      {
        if (appData.appDefinition !== null)
        {
          flowableAppDetailed = appData.appDefinition; // Flowable API wraps the app data inside "appDefinition".
          const key = appData.appDefinition.key;
          const id = appData.appDefinition.id;

          // step 3: get the deployed BPMN by the app's key.
          flowableServiceDetailed.getDeployedBpmnByAppKey(key).subscribe({
            next: (bpmn) =>
            {
              this.deployedBpmn = bpmn.data[0]; // Flowable API wraps the models in a data[] array
              messageHandlerServiceDetailed.displaySuccess(
                'DEPLOYED-SUCCESSFULLY'
              );

              // step 4: save the deployed BPMN id and key in channel.
              this.deployedBpmn.appId = id;
              channelServiceDetailed.updateModel(channelDetailed.id, this.deployedBpmn).subscribe({
                next: (channel) =>
                {
                  channelDetailed = channel;
                }
              });
            },
            error: (error) =>
            {
              this.logger.error(error);
              messageHandlerServiceDetailed.displayError('FAILED-TO-DEPLOY-PROCESS');
            }
          });
        }
        else if (appData.error)
        {
          messageHandlerServiceDetailed.displayError('FAILED-TO-VALIDATE-PROCESS');
        }
      },
      error: (error) =>
      {
        this.logger.error(error);
        messageHandlerServiceDetailed.displayError('FAILED-TO-DEPLOY-PROCESS');
      }
    });
  }

  public manageLoad(): void
  {
    if (this.channel)
    {
      this.modelCommunication('authentication');
      setTimeout(() =>
      {
        this.idInterval = setInterval(() =>
        {
          this.modelCommunication('autosave');
        }, this.modelerAutoSave);
      }, this.modelerAutoSave);
    }
  }

  public modelCommunication(type: string): void
  {
    // Prevent in case iframe is not defined yet
    if (this.iframe === undefined)
    {
      return;
    }

    const nativeEl = this.iframe.nativeElement;
    const cont = nativeEl.contentWindow || nativeEl.contentDocument;
    if (cont !== undefined && cont !== null)
    {
      if (type === 'autosave')
      {
        const channelAutosave = new ChannelData();
        channelAutosave.operation = 'save-model';
        cont.postMessage(channelAutosave, '*');
      }
      if (type === 'authentication')
      {
        const user: User = this.authService.currentUser;
        if (user !== null)
        {
          const config = this.appConfigService.getConfig();
          const baseApiUrl = config.api.baseUrl;
          const channelData = new ChannelData();
          channelData.operation = 'authentication';
          channelData.token = user.token;
          channelData.username = user.userName;
          channelData.root = baseApiUrl + '/';
          channelData.patientLabel = this.patientLabel;
          channelData.channelWorkflowCodeSetId = this.channel.channelCategory !== null ?
            this.channel.channelCategory.channelWorkflowStatus.id : null;

          cont.postMessage(channelData, '*');
        }
      }
    }
    this.loading = false;
  }

  public ngOnDestroy(): void
  {
    if (this.idInterval)
    {
      clearInterval(this.idInterval);
    }
    this.idleService.startIdle();
  }

  public getSessionSettings(): void
  {
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.modelerAutoSave = sessionSetting.modelerAutoSave;
        this.patientLabel = sessionSetting.patientLabel;
      }
    });
  }
}
